'use client';

import { useMemo } from 'react';
import { useAuthUser } from '@/lib/auth/common/useSession';
import { hasPartnerTag } from '@/lib/constants/partner';

/**
 * Whether the signed-in user has a partner tag (impartner/cs_employee), reactively.
 *
 * Replaces three independent, mutually-inconsistent reimplementations of this same check
 * (two of them byte-for-byte copies of each other) that each read `localStorage['user']`
 * directly on mount and never updated again. Mirrors `usePartnerOverlay.ts`'s existing
 * `isLoading`-gated pattern rather than inventing a new one.
 *
 * Returns `null` while auth is still resolving (not yet a "no"), `false` once resolved and
 * either logged out or without the tag, `true` once resolved and confirmed a partner.
 */
export function useHasPartnerTag(): boolean | null {
  const { user, isLoggedIn, isLoading } = useAuthUser();

  return useMemo(() => {
    if (isLoading) return null;
    return isLoggedIn && hasPartnerTag(user);
  }, [isLoading, isLoggedIn, user]);
}
